/** Optical metrics of the selected line profile, peak table and analysis settings. */
import type { BaselineMode } from '../../core/metrics';
import { fmtLevel, metricRows, peakRows } from '../../core/report';
import { fmt, unitScale } from '../../core/units';
import { measurement, metricSettings, selectedResult, spatial, updateSignal } from '../../app/store';
import { copyText } from '../../app/exporters';
import { Button, Field, NumberField, NumberInput, Section, Select, SelectField, Segmented } from '../controls';

const METRIC_HELP: Record<string, string> = {
  Maximum: 'metric.max',
  Minimum: 'metric.min',
  Mean: 'metric.mean',
  Median: 'metric.median',
  'Standard deviation': 'metric.sd',
  RMS: 'metric.rms',
  'Coefficient of variation': 'metric.cv',
  'Michelson contrast': 'metric.michelson',
  'Uniformity U0 = min/mean': 'metric.u0',
  'Uniformity U1 = min/max': 'metric.u1',
  'Non-uniformity (max−min)/mean': 'metric.nonuni',
  'Integral ∫ value d(distance)': 'metric.integral',
  Centroid: 'metric.centroid',
  'Edge transition distance': 'metric.edge',
  'Clipped samples': 'metric.clipped',
};

export function MetricsPanel() {
  const r = selectedResult.value;
  const m = measurement.value;
  const ms = metricSettings.value;
  const sp = spatial.value;
  const k = unitScale(sp);
  const unit = m?.unit ?? '';
  const rows = r ? metricRows(r.metrics, unit, sp.unit, k) : [];
  const peaks = r ? peakRows(r.metrics, sp.unit, k) : [];
  const tsv = () =>
    [
      ['Metric', 'Value', 'Unit'],
      ...rows.map((x) => [x[0], typeof x[1] === 'number' ? String(x[1]) : (x[1] ?? ''), x[2]]),
      [],
      ...peaks.map((p) => p.map((c) => (c === null ? '' : String(c)))),
    ]
      .map((l) => l.join('\t'))
      .join('\n');
  return (
    <div class="metrics-panel">
      {!r ? (
        <div class="muted pad">Draw or select a line to compute metrics.</div>
      ) : (
        <>
          <div class="toolbar-row">
            <b style={{ color: r.roi.color }}>{r.roi.name}</b>
            <span class="muted">
              L = {fmt(r.profile.length * k, 5)} {sp.unit} · {fmt(r.profile.angleDeg, 4)}° · {r.profile.n} samples
            </span>
            <span class="spacer" />
            <Button small icon="copy" help="metrics.copy" onClick={() => copyText(tsv(), 'Metrics copied as tab-separated text')}>
              Copy
            </Button>
          </div>
          {r.metrics.clippedSamples > 0 && (
            <div class="hint warn" data-help="metric.clipped">
              {r.metrics.clippedSamples} sample(s) touch pixels at the maximum code value (possible saturation) — peak values and widths may
              be underestimated.
            </div>
          )}
          <table class="metrics" data-testid="metrics-table">
            <tbody>
              {rows.map(([label, value, u]) => (
                <tr
                  key={label}
                  data-help={
                    METRIC_HELP[label] ??
                    (label.startsWith('Baseline') ? 'metric.baseline' : label.startsWith('Edge') ? 'metric.edge' : undefined)
                  }
                >
                  <th>{label}</th>
                  <td class="mono">{typeof value === 'number' ? fmt(value, 6) : value}</td>
                  <td class="unit">{u}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Section title={`Peaks (${r.metrics.peaks.length})`} help="metrics.peaks">
            {r.metrics.peaks.length === 0 ? (
              <div class="muted">No peak found.</div>
            ) : (
              <div class="table-scroll">
                <table class="grid" data-testid="peaks-table">
                  <thead>
                    <tr>
                      {peaks[0].map((h) => (
                        <th key={String(h)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {peaks.slice(1).map((row, i) => (
                      <tr key={i}>
                        {row.map((c, j) => (
                          <td key={j} class="mono">
                            {typeof c === 'number' ? fmt(c, 6) : (c ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
      <Section title="Analysis settings" defaultOpen={false} help="metrics.settings">
        <SelectField<BaselineMode>
          label="Baseline"
          help="metrics.baseline"
          value={ms.baseline}
          options={[
            { value: 'min', label: 'Profile minimum' },
            { value: 'zero', label: 'Zero' },
            { value: 'edges', label: 'Mean of profile ends' },
            { value: 'edgesLinear', label: 'Linear fit through ends (sloped)' },
            { value: 'percentile', label: 'Low percentile' },
            { value: 'manual', label: 'Manual value' },
          ]}
          onChange={(v) => updateSignal(metricSettings, { baseline: v })}
        />
        {(ms.baseline === 'edges' || ms.baseline === 'edgesLinear') && (
          <NumberField
            label="End fraction"
            help="metrics.edgeFraction"
            value={ms.edgeFraction * 100}
            unit="%"
            min={1}
            max={50}
            onChange={(v) => updateSignal(metricSettings, { edgeFraction: v / 100 })}
          />
        )}
        {ms.baseline === 'percentile' && (
          <NumberField
            label="Percentile"
            help="metrics.percentile"
            value={ms.baselinePercentile}
            min={0}
            max={100}
            unit="%"
            onChange={(v) => updateSignal(metricSettings, { baselinePercentile: v })}
          />
        )}
        {ms.baseline === 'manual' && (
          <NumberField
            label="Baseline value"
            help="metrics.manualBaseline"
            value={ms.manualBaseline}
            unit={unit}
            onChange={(v) => updateSignal(metricSettings, { manualBaseline: v })}
          />
        )}
        <Field label="Analyse" help="metrics.polarity">
          <Segmented
            value={ms.polarity}
            options={[
              { value: 'peak', label: 'Peaks' },
              { value: 'dip', label: 'Dips' },
            ]}
            onChange={(v) => updateSignal(metricSettings, { polarity: v })}
          />
        </Field>
        <Field label="Report" help="metrics.peakMode">
          <Segmented
            value={ms.peakMode}
            testId="peak-mode"
            options={[
              { value: 'highest', label: 'Highest only' },
              { value: 'all', label: 'All peaks' },
            ]}
            onChange={(v) => updateSignal(metricSettings, { peakMode: v })}
          />
        </Field>
        {ms.peakMode === 'all' && (
          <>
            <NumberField
              label="Min. prominence"
              help="metrics.prominence"
              value={ms.minProminence * 100}
              unit="% of range"
              min={0}
              max={100}
              onChange={(v) => updateSignal(metricSettings, { minProminence: v / 100 })}
            />
            <NumberField
              label="Min. separation"
              help="metrics.separation"
              value={ms.minSeparation}
              unit={sp.unit}
              min={0}
              onChange={(v) => updateSignal(metricSettings, { minSeparation: v })}
            />
          </>
        )}
        <Field label="Width levels" help="metrics.widthLevels">
          <WidthLevels />
        </Field>
        <Field label="Edge levels" help="metrics.edgeLevels">
          <span class="inline">
            <NumberInput
              value={ms.edgeLow * 100}
              min={0}
              max={49}
              unit="%"
              onChange={(v) => updateSignal(metricSettings, { edgeLow: v / 100 })}
            />
            –
            <NumberInput
              value={ms.edgeHigh * 100}
              min={51}
              max={100}
              unit="%"
              onChange={(v) => updateSignal(metricSettings, { edgeHigh: v / 100 })}
            />
          </span>
        </Field>
        <NumberField
          label="Derivative smoothing σ"
          help="metrics.smoothing"
          value={ms.derivativeSmoothing}
          min={0}
          max={50}
          step={0.5}
          unit="samples"
          onChange={(v) => updateSignal(metricSettings, { derivativeSmoothing: v })}
        />
      </Section>
    </div>
  );
}

const PRESETS: { value: string; label: string; levels: number[] }[] = [
  { value: 'std', label: 'FWHM, FW10 %, 1/e²', levels: [0.5, 0.1, Math.exp(-2)] },
  { value: 'fwhm', label: 'FWHM only', levels: [0.5] },
  { value: 'wide', label: 'FWHM, FW 20 %, FW 5 %', levels: [0.5, 0.2, 0.05] },
];

function WidthLevels() {
  const ms = metricSettings.value;
  const key =
    PRESETS.find((p) => p.levels.length === ms.widthLevels.length && p.levels.every((l, i) => Math.abs(l - ms.widthLevels[i]) < 1e-9))
      ?.value ?? 'custom';
  return (
    <span class="inline wrap">
      <Select
        value={key}
        options={[...PRESETS.map((p) => ({ value: p.value, label: p.label })), { value: 'custom', label: 'Custom…' }]}
        onChange={(v) => {
          const p = PRESETS.find((x) => x.value === v);
          if (p) updateSignal(metricSettings, { widthLevels: [...p.levels] });
          else updateSignal(metricSettings, { widthLevels: [...ms.widthLevels] });
        }}
      />
      <input
        type="text"
        class="levels-input"
        title="Comma-separated percentages"
        value={ms.widthLevels.map((l) => Number((l * 100).toPrecision(4))).join(', ')}
        onChange={(e) => {
          const vals = (e.target as HTMLInputElement).value
            .split(/[,;\s]+/)
            .map(Number)
            .filter((v) => v > 0 && v < 100)
            .map((v) => v / 100);
          if (vals.length) updateSignal(metricSettings, { widthLevels: vals });
        }}
      />
      <span class="muted small">{ms.widthLevels.map(fmtLevel).join(' · ')}</span>
    </span>
  );
}
